import { useState } from 'react';
import {
  RiAlertLine,
  RiArrowRightLine,
  RiBookmarkLine,
  RiLightbulbFlashLine,
  RiPlayLine,
  RiSearchEyeLine,
  RiShieldCheckLine,
  RiUserStarLine,
} from 'react-icons/ri';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { useStore } from '../state/store';
import { AiModelPicker } from './AiModelPicker';
import { RISK_BG, RISK_LABEL, SEVERITY_BG, SEVERITY_LABEL } from './icons';
import { cn } from '@/lib/utils';

export function AssessmentPanel() {
  const aiAnalysis = useStore(state => state.aiAnalysis);
  const analyze = useStore(state => state.analyze);
  const meta = useStore(state => state.meta);
  const activeTarget = useStore(state => state.targets.find(t => t.id === state.activeTargetId));
  const [focus, setFocus] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    await analyze(focus || undefined);
    setBusy(false);
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
        <RiLightbulbFlashLine className="size-3.5 text-[var(--color-assess)]" />
        <span className="text-[12px] font-semibold">研判推演</span>
        <div className="ml-auto">
          <AiModelPicker compact />
        </div>
      </div>

      <div className="border-b border-border px-3 py-2">
        <AiModelPicker />
      </div>

      <div className="flex flex-col gap-3 p-3">
        {!activeTarget && (
          <div className="rounded-[var(--radius)] border border-dashed border-border p-4 text-[11.5px] leading-relaxed text-muted-foreground">
            先在左侧「对象」中选择一个对象。研判会综合已归档的线索、证据与风险因子，输出风险面、推断、
            下一步合规核查方向与防护建议。
          </div>
        )}

        {activeTarget && (
          <>
            <div className="flex items-center gap-2 rounded-[var(--radius)] border border-border bg-muted/40 p-2.5">
              <RiUserStarLine className="size-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-semibold">{activeTarget.name}</div>
                <div className="text-[10px] text-muted-foreground">
                  {activeTarget.role ?? '—'} · {activeTarget.org ?? '—'}
                </div>
              </div>
              <Badge variant="outline" className={cn('border-transparent', RISK_BG[activeTarget.riskLevel])}>
                风险 {activeTarget.riskScore} · {RISK_LABEL[activeTarget.riskLevel]}
              </Badge>
            </div>

            <div className="flex flex-col gap-2">
              <Input
                placeholder="研判侧重（可选），如：供应商关系 / 邮件安全"
                value={focus}
                onChange={e => setFocus(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !busy && run()}
              />
              <Button size="sm" disabled={busy} onClick={run}>
                <RiPlayLine className="size-3.5" />
                {busy ? '推演中…' : '执行研判'}
              </Button>
            </div>
          </>
        )}

        {aiAnalysis && (
          <>
            <Separator />
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              引擎：{aiAnalysis.mode === 'remote' ? '远端模型' : '本地启发式'}
              {aiAnalysis.providerError && `（远端不可用，已回退：${aiAnalysis.providerError}）`}
            </div>

            <Block title="画像摘要">
              <p className="text-[11.5px] leading-relaxed">{aiAnalysis.profileSummary}</p>
            </Block>

            {aiAnalysis.attackSurface.length > 0 && (
              <Block title="风险面" icon={<RiAlertLine className="size-3.5" />}>
                {aiAnalysis.attackSurface.map((item, index) => (
                  <div key={index} className="rounded-[var(--radius-sm)] border border-border bg-card p-2.5">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className={cn('border-transparent', SEVERITY_BG[item.severity])}>
                        {SEVERITY_LABEL[item.severity]}
                      </Badge>
                      <span className="text-[11.5px] font-semibold">{item.title}</span>
                    </div>
                    <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{item.detail}</p>
                  </div>
                ))}
              </Block>
            )}

            {aiAnalysis.behaviorHypotheses.length > 0 && (
              <Block title="推断与判断">
                {aiAnalysis.behaviorHypotheses.map((item, index) => (
                  <div key={index} className="rounded-[var(--radius-sm)] border border-border bg-card p-2.5">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="assess">{item.category}</Badge>
                      <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                        {Math.round((item.confidence ?? 0) * 100)}%
                      </span>
                    </div>
                    <p className="mt-1 text-[11.5px] leading-relaxed">{item.statement}</p>
                    {item.rationale && (
                      <p className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">{item.rationale}</p>
                    )}
                  </div>
                ))}
              </Block>
            )}

            {aiAnalysis.osintNextSteps.length > 0 && (
              <Block title="下一步合规核查" icon={<RiSearchEyeLine className="size-3.5" />}>
                <ol className="flex flex-col gap-2">
                  {aiAnalysis.osintNextSteps.map((item, index) => (
                    <li key={index} className="flex gap-2">
                      <span className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full bg-muted font-mono text-[9px]">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[11.5px] font-medium">{item.action}</div>
                        <div className="text-[10.5px] leading-relaxed text-muted-foreground">{item.rationale}</div>
                        {item.legalityNote && (
                          <div className="mt-1 flex items-start gap-1 rounded-[var(--radius-sm)] bg-primary/6 p-1.5 text-[10px] text-primary">
                            <RiShieldCheckLine className="mt-px size-3 shrink-0" />
                            <span>合规限定：{item.legalityNote}</span>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </Block>
            )}

            {aiAnalysis.pretextIdeas.length > 0 && (
              <Block title="培训场景类别（非可发送内容）" icon={<RiBookmarkLine className="size-3.5" />}>
                {aiAnalysis.pretextIdeas.map((item, index) => (
                  <div key={index} className="rounded-[var(--radius-sm)] border border-border bg-card p-2.5">
                    <div className="text-[11.5px] font-semibold">{item.scenario}</div>
                    <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                      渠道 {item.channel} · 目的 {item.objective}
                    </div>
                    <div className="mt-1 rounded-[var(--radius-sm)] border border-[var(--color-cord)]/25 bg-[var(--color-cord)]/6 p-1.5 text-[10px] text-[var(--color-cord)]">
                      {item.safetyNote}
                    </div>
                  </div>
                ))}
              </Block>
            )}

            {aiAnalysis.defensiveRecommendations.length > 0 && (
              <Block title="防护建议" icon={<RiShieldCheckLine className="size-3.5" />}>
                <ul className="flex flex-col gap-1.5">
                  {aiAnalysis.defensiveRecommendations.map((item, index) => (
                    <li key={index} className="flex items-start gap-1.5 text-[11px] leading-relaxed">
                      <RiArrowRightLine className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Block>
            )}

            <p className="border-t border-dashed border-border pt-2.5 text-[10px] leading-relaxed text-muted-foreground">
              {aiAnalysis.disclaimer}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Block({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="label-caps">{title}</span>
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}